import { Box3, Vector2, Vector3 } from 'three'

/** 2D oriented bounding box */
export interface OBB2D {
  center: Vector2
  halfExtents: [number, number]
  axes: [Vector2, Vector2]
  area: number
}

/** 3D oriented bounding box */
export interface OBB3D {
  center: Vector3
  rotation: [number, number, number, number, number, number, number, number, number] // 3x3 row-major
  extent: [number, number, number]
}

/**
 * Computes polygon-aligned oriented bounding boxes from a set of coplanar 3D points.
 */
export class MinimumBoundingBox {
  private points3D: Vector3[]

  private thickness: number

  private padding: number

  private u!: Vector3

  private v!: Vector3

  private n!: Vector3

  private origin!: Vector3

  private projected2D!: Vector2[]

  private hull2D?: Vector2[]

  private obb2D?: OBB2D

  private obb3D?: OBB3D

  private aabb3D?: Box3

  /**
   * @param points3D Array of 3D coplanar points
   * @param thickness Optional thickness along normal axis (default: 0.5)
   * @param padding Optional padding to expand OBB (default: 0)
   */
  constructor(points3D: Vector3[], thickness = 0.05, padding = 0) {
    this.points3D = points3D.map((p) => p.clone())
    this.thickness = thickness
    this.padding = padding

    this.computePlaneBasis()
    this.projected2D = this.projectTo2D()
  }

  /**
   * Get 2D OBB in plane space
   * @returns 2D oriented bounding box
   */
  public getOBB2D(): OBB2D {
    if (this.obb2D) return this.obb2D

    if (!this.hull2D) this.hull2D = this.computeConvexHull2D()

    this.obb2D = this.computeMinimumOBB2D()
    return this.obb2D
  }

  /**
   * Get 3D OBB in world space
   * @returns 3D oriented bounding box
   */
  public getOBB3D(): OBB3D {
    if (this.obb3D) return this.obb3D

    const obb2D = this.getOBB2D()

    const center3D = this.origin
      .clone()
      .add(this.u.clone().multiplyScalar(obb2D.center.x))
      .add(this.v.clone().multiplyScalar(obb2D.center.y))

    // prettier-ignore
    const rotationMatrix: OBB3D['rotation'] = [
      this.u.x, this.v.x, this.n.x,
      this.u.y, this.v.y, this.n.y,
      this.u.z, this.v.z, this.n.z,
    ]

    this.obb3D = {
      center: center3D,
      rotation: rotationMatrix,
      extent: [
        obb2D.halfExtents[0] * 2 + this.padding * 2,
        obb2D.halfExtents[1] * 2 + this.padding * 2,
        this.thickness,
      ],
    }

    return this.obb3D
  }

  /**
   * Get 3D corners of OBB for debugging/wireframe
   * @returns Array of 8 Vector3 corners
   */
  public getOBB3DCorners(): Vector3[] {
    const obb = this.getOBB3D()
    const { center, rotation, extent } = obb
    const [ex, ey, ez] = extent.map((v) => v * 0.5)

    const rotCols = [
      new Vector3(rotation[0], rotation[3], rotation[6]),
      new Vector3(rotation[1], rotation[4], rotation[7]),
      new Vector3(rotation[2], rotation[5], rotation[8]),
    ]

    const corners: Vector3[] = []

    ;[-ex, ex].forEach((dx) => {
      ;[-ey, ey].forEach((dy) => {
        ;[-ez, ez].forEach((dz) => {
          const corner = center
            .clone()
            .add(rotCols[0].clone().multiplyScalar(dx))
            .add(rotCols[1].clone().multiplyScalar(dy))
            .add(rotCols[2].clone().multiplyScalar(dz))
          corners.push(corner)
        })
      })
    })

    return corners
  }

  /**
   * Get 2D corners of the polygon-aligned OBB
   * @returns Array of 4 Vector2 corners in plane space
   */
  public getOBB2DCorners(): Vector2[] {
    const obb = this.getOBB2D()
    const { center, halfExtents, axes } = obb
    const [hx, hy] = halfExtents
    const [axisX, axisY] = axes

    const corners: Vector2[] = []

    ;[-hx, hx].forEach((dx) => {
      ;[-hy, hy].forEach((dy) => {
        const corner = center.clone().add(axisX.clone().multiplyScalar(dx)).add(axisY.clone().multiplyScalar(dy))
        corners.push(corner)
      })
    })

    return corners
  }

  /**
   * Compute polygon-aligned plane basis
   * - Primary axis `u` along longest polygon edge
   * - `v` perpendicular in-plane
   * - `n` normal
   */
  private computePlaneBasis(): void {
    this.origin = this.points3D[0].clone()

    if (this.points3D.length >= 3) {
      const v0 = this.points3D[1].clone().sub(this.points3D[0])
      const v1 = this.points3D[2].clone().sub(this.points3D[0])
      this.n = v0.clone().cross(v1).normalize()
    } else {
      this.n = new Vector3(0, 0, 1)
    }

    let maxLength = 0
    let primaryU = new Vector3(1, 0, 0)

    for (let i = 0; i < this.points3D.length; i += 1) {
      const a = this.points3D[i]
      const b = this.points3D[(i + 1) % this.points3D.length]
      const len = a.distanceTo(b)
      if (len > maxLength) {
        maxLength = len
        primaryU = b.clone().sub(a).normalize()
      }
    }

    this.u = primaryU
    this.v = this.n.clone().cross(this.u).normalize()
  }

  /**
   * Project 3D points onto plane axes
   * @returns Array of 2D points
   */
  private projectTo2D(): Vector2[] {
    return this.points3D.map((p) => {
      const local = p.clone().sub(this.origin)
      return new Vector2(local.dot(this.u), local.dot(this.v))
    })
  }

  /**
   * Compute convex hull of projected 2D points
   * @returns Convex hull points in CCW order
   */
  private computeConvexHull2D(): Vector2[] {
    const points = this.projected2D.slice()
    if (points.length <= 3) return points

    points.sort((a, b) => (a.x !== b.x ? a.x - b.x : a.y - b.y))

    const cross = (o: Vector2, a: Vector2, b: Vector2) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x)

    const lower: Vector2[] = []
    points.forEach((p) => {
      while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop()
      lower.push(p)
    })

    const upper: Vector2[] = []
    for (let i = points.length - 1; i >= 0; i -= 1) {
      const p = points[i]
      while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop()
      upper.push(p)
    }

    lower.pop()
    upper.pop()
    return lower.concat(upper)
  }

  /**
   * Compute minimum-area bounding rectangle along polygon axes
   * @returns 2D OBB
   */
  private computeMinimumOBB2D(): OBB2D {
    const points = this.hull2D!
    let minX = Infinity
    let maxX = -Infinity
    let minY = Infinity
    let maxY = -Infinity

    points.forEach((p) => {
      if (p.x < minX) minX = p.x
      if (p.x > maxX) maxX = p.x
      if (p.y < minY) minY = p.y
      if (p.y > maxY) maxY = p.y
    })

    // Apply padding
    minX -= this.padding
    maxX += this.padding
    minY -= this.padding
    maxY += this.padding

    const center = new Vector2((minX + maxX) / 2, (minY + maxY) / 2)
    const halfExtents: [number, number] = [(maxX - minX) / 2, (maxY - minY) / 2]

    return {
      center,
      halfExtents,
      axes: [new Vector2(1, 0), new Vector2(0, 1)],
      area: (maxX - minX) * (maxY - minY),
    }
  }

  getAABB(): Box3 {
    if (this.aabb3D) return this.aabb3D

    const obb = this.getOBB3D()
    const r = obb.rotation
    const ex = obb.extent[0] * 0.5
    const ey = obb.extent[1] * 0.5
    const ez = obb.extent[2] * 0.5

    // Transform OBB half-extents by rotation matrix to get AABB extents
    // Rotation is row-major: [u.x, v.x, n.x, u.y, v.y, n.y, u.z, v.z, n.z]
    const ax = Math.abs(r[0] * ex) + Math.abs(r[1] * ey) + Math.abs(r[2] * ez)
    const ay = Math.abs(r[3] * ex) + Math.abs(r[4] * ey) + Math.abs(r[5] * ez)
    const az = Math.abs(r[6] * ex) + Math.abs(r[7] * ey) + Math.abs(r[8] * ez)

    this.aabb3D = new Box3()
    this.aabb3D.min.set(obb.center.x - ax, obb.center.y - ay, obb.center.z - az)
    this.aabb3D.max.set(obb.center.x + ax, obb.center.y + ay, obb.center.z + az)

    return this.aabb3D
  }

  /**
   * Test whether this minimum bounding box intersects the given AABB (Box3).
   *
   * Uses:
   * 1. AABB–AABB overlap test (quick reject)
   * 2. Polygon vertex inside-box test (quick accept)
   * 3. Polygon plane vs box intersection test (quick reject)
   * 4. OBB-AABB SAT (Separating Axis Theorem) test
   */
  intersectsBox(bbox: Box3): boolean {
    const aabb = this.getAABB()

    // --- Quick reject: bounding boxes don't overlap at all ---
    if (!aabb.intersectsBox(bbox)) return false

    // --- Quick accept: any polygon vertex already inside the AABB ---
    for (let i = 0; i < this.points3D.length; i += 1) {
      if (bbox.containsPoint(this.points3D[i])) return true
    }

    // --- SAT check against OBB ---
    // Get OBB3D for SAT tests
    const obb = this.getOBB3D()
    const obbCenter = obb.center
    const obbExtent = obb.extent
    const obbAxes = [
      new Vector3(obb.rotation[0], obb.rotation[3], obb.rotation[6]),
      new Vector3(obb.rotation[1], obb.rotation[4], obb.rotation[7]),
      new Vector3(obb.rotation[2], obb.rotation[5], obb.rotation[8]),
    ]

    // AABB axes
    const aabbAxes = [new Vector3(1, 0, 0), new Vector3(0, 1, 0), new Vector3(0, 0, 1)]
    const aabbCenter = bbox.getCenter(new Vector3())
    const aabbExtent = bbox.getSize(new Vector3()).multiplyScalar(0.5)

    // Test all 15 potential separating axes
    const testAxes = [
      ...obbAxes, // OBB's 3 axes
      ...aabbAxes, // AABB's 3 axes
      // 9 cross products between OBB and AABB axes
      ...obbAxes
        .flatMap((obbAxis) => aabbAxes.map((aabbAxis) => obbAxis.clone().cross(aabbAxis).normalize()))
        .filter((axis) => axis.lengthSq() > 1e-6), // Filter out zero-length axes
    ]

    const centerSeparation = obbCenter.clone().sub(aabbCenter)

    for (let axisIndex = 0; axisIndex < testAxes.length; axisIndex += 1) {
      const axis = testAxes[axisIndex]

      // Project OBB onto axis
      let obbProjection = 0
      for (let i = 0; i < 3; i += 1) {
        obbProjection += Math.abs(obbAxes[i].dot(axis)) * obbExtent[i] * 0.5
      }

      // Project AABB onto axis
      const aabbProjection =
        Math.abs(aabbExtent.x * axis.x) + Math.abs(aabbExtent.y * axis.y) + Math.abs(aabbExtent.z * axis.z)

      // Check separation
      const centerProjection = Math.abs(centerSeparation.dot(axis))
      if (centerProjection > obbProjection + aabbProjection) {
        return false // Separating axis found
      }
    }

    return true // No separating axis found, boxes intersect
  }

  /**
   * Tests whether this minimum bounding box contains the given point.
   *
   * @param point3D Point in 3D space
   * @returns True if point is inside the bounding box
   */
  containsPoint(point: Vector3): boolean {
    const obb = this.getOBB3D()
    const dir = point.clone().sub(obb.center)
    const r = obb.rotation

    // prettier-ignore
    const axes = [
    new Vector3(r[0], r[1], r[2]),
    new Vector3(r[3], r[4], r[5]),
    new Vector3(r[6], r[7], r[8]),
  ]

    // Compare against half-extents since extent represents full size
    for (let i = 0; i < 3; i += 1) {
      if (Math.abs(dir.dot(axes[i])) > obb.extent[i] * 0.5) {
        return false
      }
    }

    return true
  }

  /**
   * Tests whether this minimum bounding box contains any of the given points.
   *
   * @param points3D Array of 3D points
   * @returns True if any point is inside the bounding box
   */
  containsAnyPoint(points3D: Vector3[]): boolean {
    for (let i = 0; i < points3D.length; i += 1) {
      if (this.containsPoint(points3D[i])) {
        return true
      }
    }
    return false
  }
}
